import {
  ChangeEvent,
  LegacyRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { ICONS_TYPES } from '../Icon';

import { Sizes, StyleTypes } from './enums';
import {
  AttentionIcon,
  ErrorMessage,
  ErrorMessageContainer,
  Root,
  StyledFormControlLabel,
  StyledSwitch,
} from './styles';
import { Props } from './types';

const Switcher = (
  {
    className,
    checked,
    disabled,
    error,
    label,
    loading,
    name,
    onChange,
    readOnly = false,
    required,
    size = Sizes.medium,
    styleType = StyleTypes.primary,
    indeterminate = false,
  }: Props,
  ref: unknown,
) => {
  const disabledOrLoading = disabled || loading;
  const switchRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!readOnly) {
        onChange?.(e, e.target.checked);
      }
    },
    [onChange, readOnly],
  );

  useEffect(() => {
    if (switchRef.current) {
      switchRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <Root className={className} ref={ref as LegacyRef<HTMLInputElement>}>
      <StyledFormControlLabel
        checked={checked}
        control={
          <StyledSwitch
            disabled={disabled}
            onChange={handleChange}
            readOnly={readOnly}
            inputRef={switchRef}
            size={size}
            $styleType={styleType}
            value={checked}
          />
        }
        disabled={disabledOrLoading}
        error={error}
        label={label}
        name={name}
        required={required}
        sizetype={size}
        className={className}
      />
      {!!error && (
        <ErrorMessageContainer sizetype={size}>
          <AttentionIcon icon={ICONS_TYPES.Attention} size={20} />
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </Root>
  );
};

export type { Props };
export { Sizes, StyleTypes };

export default forwardRef(Switcher);
